import React from 'react';
import { CONNECTION_STATUS, SIGNAL_STRENGTH } from './pingMonitor';

/**
 * WiFi Icon Component with individual bar colors
 * แสดงสัญญาณ WiFi โดยสามารถกำหนดสีแต่ละขีดได้
 */
const WifiIcon = ({ 
  status = CONNECTION_STATUS.ONLINE, 
  strength = SIGNAL_STRENGTH.EXCELLENT,
  size = 18,
  activeColor = '#10b981',
  inactiveColor = '#374151'
}) => {
  // กำหนดจำนวนขีดที่จะแสดงตามระดับสัญญาณ
  const getActiveBars = () => {
    if (status === CONNECTION_STATUS.OFFLINE) return 0;
    
    switch (strength) {
      case SIGNAL_STRENGTH.EXCELLENT:
        return 4;
      case SIGNAL_STRENGTH.GOOD:
        return 3;
      case SIGNAL_STRENGTH.FAIR:
        return 2;
      case SIGNAL_STRENGTH.POOR:
        return 1;
      default:
        return 0;
    }
  };

  const activeBars = getActiveBars();

  // แต่ละขีดของสัญญาณ WiFi (จากใกล้ไปไกล)
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      fill="none">
      
      {/* จุดตรงกลาง (router) - แสดงเสมอ */}
      <circle 
        cx="12" 
        cy="18" 
        r="1.5" 
        fill={activeBars >= 1 ? activeColor : inactiveColor}
      />

      {/* ขีดที่ 1 - ใกล้ที่สุด */}
      <path
        d="M8.5 15.5 C9.5 14.5, 10.2 14, 12 14 C13.8 14, 14.5 14.5, 15.5 15.5"
        stroke={activeBars >= 1 ? activeColor : inactiveColor}
        strokeWidth="2"
        strokeLinecap="round"
        fill="none"
      />

      {/* ขีดที่ 2 */}
      <path
        d="M6 12.5 C8 10.5, 9.5 9.5, 12 9.5 C14.5 9.5, 16 10.5, 18 12.5"
        stroke={activeBars >= 2 ? activeColor : inactiveColor}
        strokeWidth="2"
        strokeLinecap="round"
        fill="none"
      />

      {/* ขีดที่ 3 */}
      <path
        d="M3.5 9.5 C6.5 6.5, 8.5 5, 12 5 C15.5 5, 17.5 6.5, 20.5 9.5"
        stroke={activeBars >= 3 ? activeColor : inactiveColor}
        strokeWidth="2"
        strokeLinecap="round"
        fill="none"
      />

      {/* ขีดที่ 4 - ไกลที่สุด */}
      <path
        d="M1 6.5 C5 2.5, 7.5 1, 12 1 C16.5 1, 19 2.5, 23 6.5"
        stroke={activeBars >= 4 ? activeColor : inactiveColor}
        strokeWidth="2"
        strokeLinecap="round"
        fill="none"
      />

      {/* เส้นขีดทับ (X) เมื่อ offline */}
      {status === CONNECTION_STATUS.OFFLINE && (
        <line
          x1="3"
          y1="3"
          x2="21"
          y2="21"
          stroke="#ef4444"
          strokeWidth="2"
          strokeLinecap="round"
        />
      )}
    </svg>
  );
};

/**
 * WiFi Icon Component แบบเรียบง่าย - ใช้ bars แนวตั้ง
 */
export const SimpleWifiIcon = ({ 
  status = CONNECTION_STATUS.ONLINE, 
  strength = SIGNAL_STRENGTH.EXCELLENT,
  size = 18,
  activeColor = '#10b981',
  inactiveColor = 'rgba(255, 255, 255, 0.2)'
}) => {
  const getActiveBars = () => {
    if (status === CONNECTION_STATUS.OFFLINE) return 0;
    
    switch (strength) {
      case SIGNAL_STRENGTH.EXCELLENT:
        return 4;
      case SIGNAL_STRENGTH.GOOD:
        return 3;
      case SIGNAL_STRENGTH.FAIR:
        return 2;
      case SIGNAL_STRENGTH.POOR:
        return 1;
      default:
        return 0;
    }
  };

  const activeBars = getActiveBars();

  const barConfig = [
    { height: 6, x: 2 },
    { height: 10, x: 7 },
    { height: 14, x: 12 },
    { height: 18, x: 17 }
  ];

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg">
      
      {barConfig.map((bar, index) => (
        <rect
          key={index}
          x={bar.x}
          y={24 - bar.height - 2}
          width="3"
          height={bar.height}
          rx="1.5"
          fill={activeBars > index ? activeColor : inactiveColor}
        />
      ))}

      {status === CONNECTION_STATUS.OFFLINE && (
        <>
          <line
            x1="2"
            y1="2"
            x2="22"
            y2="22"
            stroke="#ef4444"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <line
            x1="22"
            y1="2"
            x2="2"
            y2="22"
            stroke="#ef4444"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </>
      )}
    </svg>
  );
};

/**
 * WiFi Icon แบบมีสี gradient สวยงาม
 */
export const GradientWifiIcon = ({ 
  status = CONNECTION_STATUS.ONLINE, 
  strength = SIGNAL_STRENGTH.EXCELLENT,
  size = 18
}) => {
  const getActiveBars = () => {
    if (status === CONNECTION_STATUS.OFFLINE) return 0;
    
    switch (strength) {
      case SIGNAL_STRENGTH.EXCELLENT:
        return 4;
      case SIGNAL_STRENGTH.GOOD:
        return 3;
      case SIGNAL_STRENGTH.FAIR:
        return 2;
      case SIGNAL_STRENGTH.POOR:
        return 1;
      default:
        return 0;
    }
  };

  const activeBars = getActiveBars();

  const getColor = (barIndex) => {
    if (activeBars === 0) return '#4b5563';
    if (barIndex >= activeBars) return '#374151';
    
    // สีจากเขียว -> เหลือง -> แดง
    if (activeBars >= 3) return '#10b981'; // เขียว
    if (activeBars === 2) return '#f59e0b'; // เหลือง
    return '#ef4444'; // แดง
  };

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      fill="none">
      
      <defs>
        <linearGradient id="wifiGradient1" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={getColor(0)} stopOpacity="1" />
          <stop offset="100%" stopColor={getColor(0)} stopOpacity="0.6" />
        </linearGradient>
        <linearGradient id="wifiGradient2" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={getColor(1)} stopOpacity="1" />
          <stop offset="100%" stopColor={getColor(1)} stopOpacity="0.6" />
        </linearGradient>
        <linearGradient id="wifiGradient3" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={getColor(2)} stopOpacity="1" />
          <stop offset="100%" stopColor={getColor(2)} stopOpacity="0.6" />
        </linearGradient>
        <linearGradient id="wifiGradient4" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={getColor(3)} stopOpacity="1" />
          <stop offset="100%" stopColor={getColor(3)} stopOpacity="0.6" />
        </linearGradient>
      </defs>

      <circle cx="12" cy="18" r="1.5" fill="url(#wifiGradient1)" />
      
      <path
        d="M8.5 15.5 C9.5 14.5, 10.2 14, 12 14 C13.8 14, 14.5 14.5, 15.5 15.5"
        stroke="url(#wifiGradient1)"
        strokeWidth="2"
        strokeLinecap="round"
      />

      <path
        d="M6 12.5 C8 10.5, 9.5 9.5, 12 9.5 C14.5 9.5, 16 10.5, 18 12.5"
        stroke="url(#wifiGradient2)"
        strokeWidth="2"
        strokeLinecap="round"
      />

      <path
        d="M3.5 9.5 C6.5 6.5, 8.5 5, 12 5 C15.5 5, 17.5 6.5, 20.5 9.5"
        stroke="url(#wifiGradient3)"
        strokeWidth="2"
        strokeLinecap="round"
      />

      <path
        d="M1 6.5 C5 2.5, 7.5 1, 12 1 C16.5 1, 19 2.5, 23 6.5"
        stroke="url(#wifiGradient4)"
        strokeWidth="2"
        strokeLinecap="round"
      />

      {status === CONNECTION_STATUS.OFFLINE && (
        <line
          x1="3"
          y1="3"
          x2="21"
          y2="21"
          stroke="#ef4444"
          strokeWidth="2.5"
          strokeLinecap="round"
        />
      )}
    </svg>
  );
};

export default WifiIcon;

